/**
 * Report dashboard — POST Statistics/GetAgentStatistics + GetAgentStatisticsStatus
 */
import {
  GetAgentStatistics,
  GetAgentStatisticsStatus,
} from "../Source/medicalAgent/index.js";
import {
  getSessionAgentId,
  POLICY_STATUS_ID_BY_KEY,
  POLICY_STATUS_ORDER,
  policyStatusLabelFromId,
  resolveStatusKeyFromStatisticRow,
} from "./agentRequestsApi.js";

function toApiDateTimeStart(dateStr) {
  const s = String(dateStr || "").trim();
  if (!s) return undefined;
  return s.length <= 10 ? `${s}T00:00:00` : s;
}

function toApiDateTimeEnd(dateStr) {
  const s = String(dateStr || "").trim();
  if (!s) return undefined;
  return s.length <= 10 ? `${s}T23:59:59` : s;
}

export function buildAgentStatisticsPayload(dateFrom, dateTo) {
  const payload = {
    requestCorrelationId: crypto.randomUUID(),
  };
  const agentId = getSessionAgentId();
  if (agentId != null) {
    payload.agentId = agentId;
  }
  const from = toApiDateTimeStart(dateFrom);
  const to = toApiDateTimeEnd(dateTo);
  if (from) payload.dateFrom = from;
  if (to) payload.dateTo = to;
  return payload;
}

function pickStatisticsData(response) {
  const body = response?.data;
  if (!body || body.isSuccess === false) return null;
  return body.data ?? body.Data ?? null;
}

function pickStatusStatisticsRows(response) {
  const body = response?.data;
  if (!body || body.isSuccess === false) return [];
  const raw = body.data ?? body.Data;
  return Array.isArray(raw) ? raw : [];
}

export function mapApiReportSummary(data) {
  if (!data || typeof data !== "object") {
    return { salesCount: 0, premiumSum: 0, insuredCount: 0, productsUsedCount: 0 };
  }
  return {
    salesCount: Number(data.allCount ?? data.AllCount ?? 0) || 0,
    premiumSum: Number(data.allPremium ?? data.AllPremium ?? 0) || 0,
    insuredCount: Number(data.allInsured ?? data.AllInsured ?? 0) || 0,
    productsUsedCount: Number(data.allProducts ?? data.AllProducts ?? 0) || 0,
  };
}

export function mapApiReportByProduct(data) {
  const rows = data?.productStatistics ?? data?.ProductStatistics ?? [];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      productName: String(row.prodName ?? row.ProdName ?? "—").trim() || "—",
      salesCount: Number(row.reqCount ?? row.ReqCount ?? 0) || 0,
      premiumSum: Number(row.premiumSum ?? row.PremiumSum ?? 0) || 0,
    }))
    .sort((a, b) => b.salesCount - a.salesCount);
}

export function mapApiReportByStatus(rows) {
  const counts = Object.fromEntries(POLICY_STATUS_ORDER.map((key) => [key, 0]));
  const statusNames = Object.fromEntries(POLICY_STATUS_ORDER.map((key) => [key, null]));
  let periodTotal = 0;

  for (const row of rows || []) {
    const count = Number(row.reqCount ?? row.ReqCount ?? 0) || 0;
    if (count <= 0) continue;
    const statusKey = resolveStatusKeyFromStatisticRow(row);
    if (!statusKey || counts[statusKey] == null) continue;
    counts[statusKey] += count;
    periodTotal += count;
    const name = String(row.statusName ?? row.StatusName ?? "").trim();
    if (name && !statusNames[statusKey]) {
      statusNames[statusKey] = name;
    }
  }

  const safeDenom = periodTotal > 0 ? periodTotal : 1;

  return POLICY_STATUS_ORDER.map((statusKey) => {
    const count = counts[statusKey] ?? 0;
    const statusId = POLICY_STATUS_ID_BY_KEY[statusKey];
    return {
      statusKey,
      statusId,
      statusName: statusNames[statusKey] || policyStatusLabelFromId(statusId),
      count,
      periodTotal,
      shareOfTotal: Math.round((count / safeDenom) * 1000) / 10,
    };
  });
}

export async function loadAgentReportStatistics(dateFrom, dateTo) {
  const payload = buildAgentStatisticsPayload(dateFrom, dateTo);
  const [statsRes, statusRes] = await Promise.all([
    GetAgentStatistics(payload),
    GetAgentStatisticsStatus(payload),
  ]);

  const statsData = pickStatisticsData(statsRes);
  const statusRows = pickStatusStatisticsRows(statusRes);

  return {
    summary: mapApiReportSummary(statsData),
    byProduct: mapApiReportByProduct(statsData),
    byStatus: mapApiReportByStatus(statusRows),
  };
}
